/**
 * Client-side session accumulator for the signal plugin.
 *
 * Maintains per-session state: interaction buffers, DOM elements,
 * rolling behavioral context window, and builds LiveAnalysisRequest
 * payloads ready to send to the backend.
 *
 * Adapted from:
 *   external-api/src/events/listeners/inference/sessionAccumulator.ts
 */

import { parseAxosEvents } from "./axosParser";
import { extractAxosElementsFromEvents } from "./elementExtractor";
import { buildBehavioralVector } from "./behavioralContextBuilder";
import type {
  InteractionBuffers,
  ElementData,
  LiveAnalysisRequest,
  ClickPosition,
} from "./types";
import { createEmptyBuffers } from "./types";

const MAX_CONTEXT_WINDOW = 10;
const SNAPSHOT_WINDOW_MS = 2000;
const CLICK_ATTRIBUTION_WINDOW_MS = 5000;
const CLICK_PRIORITY_WINDOW_MS = 30_000;
const CLICK_FRESHNESS_AFTER_SNAPSHOT_MS = 2000;

export interface AccumulatorState {
  buffers: InteractionBuffers;
  contextWindow: number[][];
  elements: ElementData[];
  url: string;
  title: string;
}

export interface ProcessResult {
  request: LiveAnalysisRequest;
}

export class SignalAccumulator {
  private state: AccumulatorState;

  constructor() {
    this.state = {
      buffers: createEmptyBuffers(),
      contextWindow: [],
      elements: [],
      url: typeof location !== "undefined" ? location.href : "",
      title: typeof document !== "undefined" ? document.title : "",
    };
  }

  /**
   * Process a batch of axos events and return a LiveAnalysisRequest
   * if enough data has accumulated, or null if not ready yet.
   */
  processEvents(events: Record<string, unknown>[]): ProcessResult | null {
    parseAxosEvents(events, this.state.buffers);

    const priorityClicks = this.getRecentClickPositions(
      this.state.buffers.latestTimestamp,
      CLICK_PRIORITY_WINDOW_MS,
    );
    const prevElements = this.state.elements;

    this.state.elements = extractAxosElementsFromEvents(
      events,
      this.state.elements,
      priorityClicks,
    );

    if (this.state.elements !== prevElements && prevElements.length > 0) {
      const freshCutoff =
        this.state.buffers.latestTimestamp - CLICK_FRESHNESS_AFTER_SNAPSHOT_MS;
      this.state.buffers.clicks = this.state.buffers.clicks.filter(
        (c) => c.timestamp >= freshCutoff,
      );
    }

    if (typeof location !== "undefined") {
      this.state.url = location.href;
    }
    if (typeof document !== "undefined") {
      this.state.title = document.title;
    }

    const windowEnd = this.state.buffers.latestTimestamp;
    const windowStart = Math.max(
      windowEnd - SNAPSHOT_WINDOW_MS,
      this.state.buffers.sessionStartTimestamp,
    );

    if (windowEnd <= 0 || windowStart >= windowEnd) {
      return null;
    }

    const vector = buildBehavioralVector(
      this.state.buffers,
      windowStart,
      windowEnd,
      this.state.elements,
    );

    this.state.contextWindow.push(vector);
    if (this.state.contextWindow.length > MAX_CONTEXT_WINDOW) {
      this.state.contextWindow =
        this.state.contextWindow.slice(-MAX_CONTEXT_WINDOW);
    }

    const clickPositions = this.getRecentClickPositions(windowEnd);

    return {
      request: {
        url: this.state.url,
        title: this.state.title,
        elements:
          this.state.elements.length > 0
            ? this.state.elements
            : [this.pageFallbackElement()],
        viewport: this.state.buffers.viewport,
        behavioral_context: this.state.contextWindow,
        click_positions: clickPositions.length > 0 ? clickPositions : undefined,
      },
    };
  }

  /** Whether there's any accumulated context to send. */
  hasData(): boolean {
    return this.state.contextWindow.length > 0;
  }

  /** Build the current request without processing new events. */
  buildCurrentRequest(): LiveAnalysisRequest | null {
    if (this.state.contextWindow.length === 0) return null;

    const clickPositions = this.getRecentClickPositions(
      this.state.buffers.latestTimestamp,
    );

    return {
      url: this.state.url,
      title: this.state.title,
      elements:
        this.state.elements.length > 0
          ? this.state.elements
          : [this.pageFallbackElement()],
      viewport: this.state.buffers.viewport,
      behavioral_context: this.state.contextWindow,
      click_positions: clickPositions.length > 0 ? clickPositions : undefined,
    };
  }

  reset(): void {
    this.state = {
      buffers: createEmptyBuffers(),
      contextWindow: [],
      elements: [],
      url: typeof location !== "undefined" ? location.href : "",
      title: typeof document !== "undefined" ? document.title : "",
    };
  }

  private pageFallbackElement(): ElementData {
    return {
      tag: "div",
      x: 0,
      y: 0,
      width: this.state.buffers.viewport.width,
      height: this.state.buffers.viewport.height,
      text: "Page",
    };
  }

  private getRecentClickPositions(
    windowEnd: number,
    windowMs: number = CLICK_ATTRIBUTION_WINDOW_MS,
  ): ClickPosition[] {
    const cutoff = windowEnd - windowMs;
    return this.state.buffers.clicks
      .filter((c) => c.timestamp >= cutoff && c.timestamp <= windowEnd)
      .map((c) => ({ x: c.x, y: c.y, timestamp: c.timestamp }));
  }
}
